import { Menu as MenuIcon } from '@mui/icons-material';
import { Box, Divider, IconButton, Menu, MenuItem } from '@mui/material';
import { GameMenuItem } from 'app/components/Sidebar/GameMenu/GameMenuItem';
import { ProfileTile } from 'app/components/Sidebar/ProfileTile';
import { SidebarHeader } from 'app/components/Sidebar/SidebarHeader';
import { SidebarItem } from 'app/components/Sidebar/SidebarItem';
import { SidebarMenuLink } from 'app/components/Sidebar/SidebarMenuLink';
import { RightMenuNavigation } from 'app/enums';
import {
  useAdminMenuItems,
  useGameMenuItems,
  useGeneralMenuItems,
  useTournamentMenuItems,
} from 'app/hooks/left-side-menu';
import { useMobileSideMenu } from 'app/hooks/mobile-side-menu';
import { useRightSideMenu } from 'app/hooks/right-side-menu';
import { GameNavigationMenuItem, NavigationMenuItem } from 'app/interfaces/sidebar';
import { RightSidebarContext } from 'app/providers';
import LogoutPopup from 'container/components/LogoutPopup';
import { isBoolean } from 'lodash';
import React, { FC, useCallback, useContext } from 'react';
import { ActionGuard } from 'shared/components/ActionGuard';
import { useStyles } from './MobileSidebar.styles';

export const MobileSidebar: FC = () => {
  const { classes } = useStyles();
  const { setActiveTab } = useContext(RightSidebarContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onOpenHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
    [setAnchorEl]
  );
  const onCloseHandler = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const {
    myProfileMenu,
    authorizedActionsMenu,
    nonAuthorizedActionsMenu,
    isLogoutOpen,
    onLogoutHandler,
  } = useRightSideMenu();

  const { generalMenuItems } = useGeneralMenuItems();
  const { tournamentMenuItems } = useTournamentMenuItems();
  const { gameMenuItems } = useGameMenuItems();
  const { adminMenuItems } = useAdminMenuItems();
  const { policesMenu } = useMobileSideMenu();

  const onClickHandler = useCallback(
    (menuItem: NavigationMenuItem) => {
      if (!isBoolean(menuItem.hideOnClick) || menuItem.hideOnClick) {
        onCloseHandler();
        setActiveTab(RightMenuNavigation.Home);
      }

      menuItem?.onClick();
    },
    [onCloseHandler, setActiveTab]
  );

  const onEditClickHandler = useCallback(
    (menuItem: GameNavigationMenuItem) => {
      onCloseHandler();
      menuItem?.onEdit();
    },
    [onCloseHandler]
  );

  return (
    <Box>
      <Box className={classes.root}>
        <IconButton onClick={onOpenHandler} aria-label="menu">
          <MenuIcon />
        </IconButton>
      </Box>
      <Menu
        PopoverClasses={{ paper: classes.popoverPaper }}
        marginThreshold={0}
        anchorPosition={{ top: 60, left: 0 }}
        open={open}
        anchorEl={anchorEl}
        className={classes.menu}
        onClose={onCloseHandler}
      >
        <Box className={classes.profile} onClick={onCloseHandler}>
          <ProfileTile />
        </Box>
        {nonAuthorizedActionsMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <SidebarItem
                className={classes.navigationItem}
                item={item}
                onClick={onClickHandler}
              />
              <Divider />
            </MenuItem>
          </Box>
        ))}
        {generalMenuItems.map((item) => (
          <ActionGuard key={item.label} disabled={item?.isAllowedWithoutAuthorization}>
            <SidebarItem
              item={item}
              endIcon={item.icon}
              onClick={onClickHandler}
              className={classes.navigationItem}
            />
          </ActionGuard>
        ))}
        {Boolean(tournamentMenuItems?.length) && (
          <Box>
            <Divider />
            <SidebarHeader value="Tournament Creator" />
            {tournamentMenuItems.map((item) => (
              <ActionGuard key={item.label} disabled={item?.isAllowedWithoutAuthorization}>
                <SidebarItem
                  item={item}
                  endIcon={item.icon}
                  onClick={onClickHandler}
                  className={classes.navigationItem}
                />
              </ActionGuard>
            ))}
          </Box>
        )}

        <Divider />
        <SidebarHeader value="Games" />
        {gameMenuItems.map((item) => (
          <GameMenuItem
            item={item}
            key={item.label}
            endIcon={item.icon}
            onClick={onClickHandler}
            onEdit={onEditClickHandler}
            className={classes.gameNavigationItem}
            height={70}
          />
        ))}

        {Boolean(adminMenuItems?.length) && (
          <Box>
            <Divider />
            <SidebarHeader value="Admin" />
            {adminMenuItems.map((item) => (
              <ActionGuard key={item.label}>
                <SidebarItem
                  item={item}
                  endIcon={item.icon}
                  onClick={onClickHandler}
                  className={classes.navigationItem}
                />
              </ActionGuard>
            ))}
          </Box>
        )}

        <SidebarHeader value="My Profile" />
        {myProfileMenu.map((item) => (
          <MenuItem key={item.label}>
            <ActionGuard disabled={item?.isAllowedWithoutAuthorization}>
              <SidebarItem
                className={classes.navigationItem}
                item={item}
                endIcon={item.icon}
                onClick={onClickHandler}
              />
            </ActionGuard>
          </MenuItem>
        ))}
        <Divider />
        {policesMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <ActionGuard disabled={item?.isAllowedWithoutAuthorization}>
                <SidebarMenuLink item={item} onClick={onClickHandler} />
              </ActionGuard>
              <Divider />
            </MenuItem>
          </Box>
        ))}
        {authorizedActionsMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <SidebarItem
                className={classes.navigationItem}
                item={item}
                onClick={onClickHandler}
              />
              <Divider />
            </MenuItem>
          </Box>
        ))}
      </Menu>
      {isLogoutOpen && <LogoutPopup isOpen={isLogoutOpen} toggle={onLogoutHandler} />}
    </Box>
  );
};
